import { logger } from '../../services/logger';
import { Navigate, useLocation } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { query, where, getDocs, getDoc } from 'firebase/firestore';
import { auth, db, getTenantCollection, getTenantDoc } from '../../firebase/config';
import { Box, CircularProgress } from '@mui/material';
import { Helmet } from 'react-helmet-async';

const fetchCommunityAccountData = async (uid) => {
  const directSnap = await getDoc(getTenantDoc(db, 'accounts', uid));
  if (directSnap?.exists && directSnap.exists()) {
    return directSnap.data();
  }
  try {
    const accountsRef = getTenantCollection(db, 'accounts');
    const q = query(accountsRef, where('members', 'array-contains', uid));
    const snap = await getDocs(q);
    return !snap.empty ? snap.docs[0].data() : null;
  } catch (qErr) {
    logger.warn("Accounts query fallback skipped in ProtectedRoute:", qErr);
    return null;
  }
};

const ProtectedRoute = ({ children, requireCommunity = false, allowGuest = false }) => {
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [isCommunityMember, setIsCommunityMember] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      if (currentUser && requireCommunity) {
        try {
          const accData = await fetchCommunityAccountData(currentUser.uid);
          const isMember = Boolean(
            accData && (
              accData.isCommunityMember === 'Yes' || 
              accData.role === 'admin' || 
              accData.role === 'coach'
            )
          );
          setIsCommunityMember(isMember);
        } catch (err) {
          logger.error("Error verifying community membership:", err);
          setIsCommunityMember(false);
        }
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [requireCommunity]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress aria-label="Authenticating..." />
      </Box>
    );
  }

  if (!user) {
    if (allowGuest && location.state?.isGuest) {
      return (
        <>
          <Helmet>
            <meta name="robots" content="noindex, nofollow" />
          </Helmet>
          {children}
        </>
      );
    }
    const isTimeout = (location?.search && location.search.includes('reason=timeout')) ||
      (typeof window !== 'undefined' && window.sessionStorage.getItem('tas_timeout_reason') === 'timeout');
    const requestedPath = `${location.pathname}${location.search}${location.hash}`;
    const loginDestination = `/login?redirect=${encodeURIComponent(requestedPath)}`;
    return <Navigate to={isTimeout ? "/login?reason=timeout" : loginDestination} replace />;
  }

  if (requireCommunity && !isCommunityMember) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <>
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      {children}
    </>
  );
};

export default ProtectedRoute;
